import { Box, Typography } from "@mui/material";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import TrashIcon from "../../../DesignEditor/assets/svgs/trash.svg";
import ZipIcon from "../../../assets/TailoredGeneration/icons/zip-icon.svg";
import { useAppStore } from "../../../hooks/useStores.tsx";
import { VisuallyHiddenInput } from "../VisuallyHiddenInput.tsx";
import styles from "./ZipFileUploader.module.scss";

interface ZipFileUploaderProps {
	onUpload: (file: File) => void;
	handleRemoveFile?: () => void;
	maxSizeGB?: number;
	className?: string;
	icon?: React.ReactNode;
	uploadedFiles?: File[];
	showUploadedFiles?: boolean;
	uploadProgress?: number;
	showProgress?: boolean;
	isUploading?: boolean;
}

const ZipFileUploader: React.FC<ZipFileUploaderProps> = ({
	onUpload,
	handleRemoveFile,
	maxSizeGB = 5,
	className = "",
	icon,
	uploadedFiles,
	isUploading,
	uploadProgress = 0,
	showProgress = false,
	showUploadedFiles = false,
}) => {
	const { t } = useTranslation("translation", { keyPrefix: "zipUploader" });
	const [isDragOver, setIsDragOver] = useState(false);
	const { uiStore } = useAppStore();

	const handleDragOver = (e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDragOver(true);
	};

	const handleDragEnter = (e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDragOver(true);
	};

	const handleDragLeave = (e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDragOver(false);
	};

	const handleFileUpload = (files: File[]) => {
		const zipFile = files.find((file) => file.type === "application/zip" || file.name.endsWith(".zip"));
		if (!zipFile) {
			uiStore.showSnackBarAlert("error", t("zipFileRequired"), 5000);
			return;
		}

		if (zipFile.size > maxSizeGB * 1024 * 1024 * 1024) {
			uiStore.showSnackBarAlert("error", t("zipFileTooLarge"), 5000);
			return;
		}

		onUpload(zipFile);
	};

	const handleDrop = (e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDragOver(false);

		const files = Array.from(e.dataTransfer.files);
		if (files.length === 0) return;
		handleFileUpload(files);
	};

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const files = Array.from(e.target.files || []);
		e.target.value = "";

		if (!files.length) return;

		handleFileUpload(files);
	};

	return (
		<>
			<Box
				className={`${styles.dashedUploadArea} ${isDragOver ? styles.dragOver : ""} ${className}`}
				component="label"
				onDragOver={handleDragOver}
				onDragEnter={handleDragEnter}
				onDragLeave={handleDragLeave}
				onDrop={handleDrop}
			>
				<VisuallyHiddenInput
					type="file"
					id="zipFileInput"
					inputProps={{
						accept: "application/zip,.zip",
						multiple: false,
					}}
					onChange={handleFileChange}
				/>
				<Box className={styles.dashedUploadContent}>
					{icon ? <Box className={styles.customIcon}>{icon}</Box> : null}
					<Typography className={styles.uploadMainText}>{t("uploadText")}</Typography>
					<Typography className={styles.uploadSubText}>{t("fileSizeLimit", { size: maxSizeGB })}</Typography>
				</Box>
			</Box>
			{((showUploadedFiles && uploadedFiles && uploadedFiles.length > 0) || (showProgress && isUploading)) && (
				<Box className={styles.uploadedFileCard}>
					<Box className={styles.fileInfo}>
						<img src={ZipIcon} alt="zip" className={styles.fileIcon} />
						<Box className={styles.fileDetails}>
							<Typography className={styles.fileName}>
								{(uploadedFiles && uploadedFiles[0]?.name) || t("uploading")}
							</Typography>
							<Typography className={styles.fileSize}>
								{uploadedFiles && uploadedFiles[0]
									? `${(uploadedFiles[0].size / 1024).toFixed(0)}kb`
									: t("calculating")}
							</Typography>
						</Box>
					</Box>
					{isUploading && (
						<Box className={styles.progressContainer}>
							<Box className={styles.progressBar} style={{ width: `${uploadProgress}%` }} />
							<Typography className={styles.progressText}>{uploadProgress}%</Typography>
						</Box>
					)}
					{handleRemoveFile && !isUploading && uploadedFiles && uploadedFiles.length > 0 && (
						<>
							<img
								alt={"remove"}
								src={TrashIcon}
								className={styles.removeIcon}
								onClick={handleRemoveFile}
							/>
						</>
					)}
				</Box>
			)}
		</>
	);
};

export default ZipFileUploader;
